/**
 * ProductMetricsPage — /internal/product-metrics
 *
 * Dashboard de produto interno para o time mktvibe.
 * Mostra TTFV, activation rate e first_kpi_viewed por tenant.
 *
 * Consome: GET /internal/product-metrics
 * Resposta esperada: ProductMetricsResponse (definido abaixo).
 *
 * MOCK LOCAL (D-FE-003): o endpoint /internal/product-metrics ainda nao
 * existe no backend. Enquanto nao for entregue pelo Backend-Engineer,
 * usamos STUB_DATA. Remover o stub e ligar ao endpoint real quando
 * disponivel.
 *
 * Acesso: rota autenticada, sem restricao adicional de role em F2.
 * Em F3+ considerar restricao a role 'owner' ou header de IP interno.
 */

import { useCallback, useEffect, useState } from 'react';
import { t } from '../../lib/i18n';
import { apiClient } from '../../lib/api-client';

// ------------------------------------------------------------------ tipos

interface TenantMetricRow {
  readonly tenant_id: string;
  readonly tenant_name: string;
  readonly segment: string;
  readonly signup_at: string | null;
  readonly first_source_connected_at: string | null;
  readonly first_kpi_viewed_at: string | null;
  readonly ttfv_days: number | null;
  readonly activated: boolean;
}

interface ProductMetricsResponse {
  readonly ttfv_avg_days: number | null;
  readonly activation_rate: number | null;
  readonly first_kpi_viewed_count: number;
  readonly rows: readonly TenantMetricRow[];
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: ProductMetricsResponse }
  | { kind: 'error' };

// ------------------------------------------------------------------ stub (D-FE-003)

const STUB_DATA: ProductMetricsResponse = {
  ttfv_avg_days: null,
  activation_rate: null,
  first_kpi_viewed_count: 0,
  rows: [],
};

// ------------------------------------------------------------------ helpers

function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatPercent(rate: number | null): string {
  if (rate === null) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

// ------------------------------------------------------------------ componente

export function ProductMetricsPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const data = await apiClient.get<ProductMetricsResponse>('/internal/product-metrics');
      setState({ kind: 'ready', data });
    } catch {
      // Fallback para stub enquanto endpoint nao existe (D-FE-003).
      // Quando o endpoint existir, remover este catch e deixar propagar.
      setState({ kind: 'ready', data: STUB_DATA });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section aria-labelledby="product-metrics-title">
      <header className="dashboard-hero">
        <h1 id="product-metrics-title">{t('productMetrics.title')}</h1>
        <p>{t('productMetrics.subtitle')}</p>
      </header>

      {state.kind === 'loading' ? <p role="status">{t('productMetrics.loading')}</p> : null}

      {state.kind === 'error' ? (
        <div role="alert" className="error">
          <p>{t('productMetrics.error')}</p>
          <button type="button" onClick={() => void load()}>
            {t('productMetrics.retryLabel')}
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {/* Cards de resumo */}
          <section className="kpi-grid" aria-label="Resumo de metricas de produto">
            <SummaryCard
              label={t('productMetrics.ttfvLabel')}
              value={
                state.data.ttfv_avg_days !== null ? `${state.data.ttfv_avg_days.toFixed(1)}` : '—'
              }
              description={t('productMetrics.ttfvDesc')}
            />
            <SummaryCard
              label={t('productMetrics.activationRateLabel')}
              value={formatPercent(state.data.activation_rate)}
              description={t('productMetrics.activationRateDesc')}
            />
            <SummaryCard
              label={t('productMetrics.firstKpiViewedLabel')}
              value={String(state.data.first_kpi_viewed_count)}
              description={t('productMetrics.firstKpiViewedDesc')}
            />
          </section>

          {/* Tabela por tenant */}
          <section aria-labelledby="metrics-table-title" className="mirror-block">
            <h2 id="metrics-table-title">Por tenant</h2>

            {state.data.rows.length === 0 ? (
              <p className="muted">{t('productMetrics.empty')}</p>
            ) : (
              <div
                className="table-wrapper"
                role="region"
                aria-label="Tabela de metricas por tenant"
                tabIndex={0}
              >
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('productMetrics.tableHeaderTenant')}</th>
                      <th scope="col">{t('productMetrics.tableHeaderSegment')}</th>
                      <th scope="col">{t('productMetrics.tableHeaderSignup')}</th>
                      <th scope="col">{t('productMetrics.tableHeaderFirstSource')}</th>
                      <th scope="col">{t('productMetrics.tableHeaderFirstKpi')}</th>
                      <th scope="col">{t('productMetrics.tableHeaderTtfv')}</th>
                      <th scope="col">{t('productMetrics.tableHeaderActivated')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.rows.map((row) => (
                      <tr key={row.tenant_id}>
                        <td>{row.tenant_name}</td>
                        <td>{row.segment}</td>
                        <td>{formatDate(row.signup_at)}</td>
                        <td>{formatDate(row.first_source_connected_at)}</td>
                        <td>{formatDate(row.first_kpi_viewed_at)}</td>
                        <td>
                          {row.ttfv_days !== null ? (
                            <span
                              className={row.ttfv_days <= 5 ? 'ttfv-met' : 'ttfv-missed'}
                              title={
                                row.ttfv_days <= 5
                                  ? t('productMetrics.ttfvMet')
                                  : t('productMetrics.ttfvMissed')
                              }
                            >
                              {row.ttfv_days.toFixed(1)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {row.activated
                            ? t('productMetrics.activated')
                            : t('productMetrics.notActivated')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------------ SummaryCard

interface SummaryCardProps {
  readonly label: string;
  readonly value: string;
  readonly description: string;
}

function SummaryCard({ label, value, description }: SummaryCardProps): JSX.Element {
  return (
    <article className="kpi-card" aria-label={label}>
      <p className="kpi-card-label">{label}</p>
      <p className="kpi-card-value" aria-label={`${label}: ${value}`}>
        {value}
      </p>
      <p className="kpi-card-desc muted">{description}</p>
    </article>
  );
}
